<template>
  <div class="landing-wrapper">
    <v-container class="py-8 py-md-12" fluid>
      <v-row justify="center">
        <v-col cols="12" lg="10">
          <v-alert v-if="error" type="error" class="mb-4" closable @click:close="error = ''">
            {{ error }}
          </v-alert>

          <div v-if="loading" class="d-flex justify-center py-8">
            <v-progress-circular indeterminate color="primary" size="48" />
          </div>

          <div v-else-if="!isAdmin" class="camp-denied">
            Esta sección es exclusiva del super administrador de Somos Teatro.
          </div>

          <template v-else-if="campaign">
            <div class="camp-title">
              <h1>{{ campaign.name }}</h1>
              <h2>DETALLE DE LA CAMPAÑA</h2>
            </div>

            <div class="camp-panel">
              <div class="camp-panel-header">
                <v-icon size="22" color="#ffffff">mdi-chart-donut</v-icon>
                <span>RESULTADOS</span>
                <span class="camp-status" :class="statusClass">
                  {{ campaign.status_display.toUpperCase() }}
                </span>
              </div>

              <div class="camp-panel-body">
                <div class="camp-metrics">
                  <div class="camp-metric">
                    <span class="camp-metric-value">{{ campaign.total_recipients }}</span>
                    <span class="camp-metric-label">CLIENTES</span>
                  </div>
                  <div class="camp-metric">
                    <span class="camp-metric-value">{{ campaign.sent_count }}</span>
                    <span class="camp-metric-label">CORREOS ENVIADOS</span>
                  </div>
                  <div class="camp-metric">
                    <span class="camp-metric-value">{{ campaign.opened_count }}</span>
                    <span class="camp-metric-label">APERTURAS</span>
                  </div>
                  <div class="camp-metric">
                    <span class="camp-metric-value">{{ campaign.reach_percent }}%</span>
                    <span class="camp-metric-label">DEL TOTAL ALCANZADO</span>
                  </div>
                </div>

                <div v-if="campaign.error" class="camp-warning">{{ campaign.error }}</div>

                <dl class="camp-summary">
                  <div>
                    <dt>FECHA DE ENVÍO</dt>
                    <dd>{{ campaign.send_date_display }}</dd>
                  </div>
                  <div>
                    <dt>OBRAS ENVIADAS</dt>
                    <dd>{{ playTitles }}</dd>
                  </div>
                  <div>
                    <dt>TIPO DE MATCH</dt>
                    <dd>{{ campaign.match_type_display }}</dd>
                  </div>
                  <div>
                    <dt>GÉNERO</dt>
                    <dd>{{ campaign.genre_name || 'TODOS' }}</dd>
                  </div>
                  <div>
                    <dt>TEMPORADA</dt>
                    <dd>{{ campaign.season_display || '-' }}</dd>
                  </div>
                  <div>
                    <dt>ESCENARIO</dt>
                    <dd>{{ campaign.stage_surface_display || 'NO DEFINIDO' }}</dd>
                  </div>
                  <div>
                    <dt>PRESUPUESTO MÁXIMO</dt>
                    <dd>{{ budgetLabel }}</dd>
                  </div>
                  <div>
                    <dt>RANGO ETÁREO</dt>
                    <dd>{{ campaign.age_range_display || '-' }}</dd>
                  </div>
                  <div>
                    <dt>TIPO DE CLIENTE</dt>
                    <dd>{{ campaign.client_type_display || 'TODOS' }}</dd>
                  </div>
                </dl>
              </div>
            </div>

            <div class="camp-panel camp-panel-spaced">
              <div class="camp-panel-header">
                <v-icon size="22" color="#ffffff">mdi-account-multiple-outline</v-icon>
                <span>DESTINATARIOS</span>
              </div>
              <div class="camp-panel-body">
                <div class="camp-table-scroll">
                  <table class="camp-table">
                    <thead>
                      <tr>
                        <th>CLIENTE</th>
                        <th>CORREO</th>
                        <th>ENVIADO</th>
                        <th>ABIERTO</th>
                        <th>CLICS</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="r in recipients" :key="r.id">
                        <td class="camp-cell-name">{{ r.name || '-' }}</td>
                        <td>{{ r.email }}</td>
                        <td>{{ formatDate(r.sent_at) }}</td>
                        <td>{{ formatDate(r.opened_at) }}</td>
                        <td>{{ r.click_count }}</td>
                      </tr>
                      <tr v-if="recipients.length === 0">
                        <td colspan="5" class="camp-empty">
                          Esta campaña todavía no tiene destinatarios.
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div class="camp-panel camp-panel-spaced">
              <div class="camp-panel-header">
                <v-icon size="22" color="#ffffff">mdi-email-outline</v-icon>
                <span>CORREO ENVIADO</span>
              </div>
              <div class="camp-panel-body">
                <p class="camp-preview-subject">
                  <span>ASUNTO:</span> {{ previewSubject || '—' }}
                </p>
                <div class="camp-preview">
                  <iframe
                    class="camp-preview-frame"
                    title="Correo de la campaña"
                    :srcdoc="previewHtml"
                  ></iframe>
                </div>
              </div>
            </div>

            <p v-if="forceError" class="camp-warning">{{ forceError }}</p>

            <div class="camp-actions">
              <button type="button" class="camp-ghost-btn" @click="goBack">
                VOLVER AL GESTOR
              </button>
              <!-- El asistente siempre deja la campaña agendada para las 09:00;
                   acá se decide adelantarla. -->
              <button
                v-if="canForceSend"
                type="button"
                class="camp-force-btn"
                :disabled="forcing"
                @click="forceSend"
              >
                {{ forcing ? 'ENVIANDO…' : 'FORZAR ENVÍO AHORA' }}
              </button>
              <button
                v-if="campaign.is_editable"
                type="button"
                class="camp-primary-btn"
                @click="goToEdit"
              >
                REVISAR CAMPAÑA
              </button>
            </div>
          </template>
        </v-col>
      </v-row>
    </v-container>

    <teatro-footer />
  </div>
</template>

<script>
import {
  fetchCampaign,
  fetchCampaignRecipients,
  forceSendCampaign,
  previewCampaignById,
} from "@/services/teatroApi";

export default {
  name: "CampaignDetail",
  props: {
    campaignId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      campaign: null,
      recipients: [],
      previewHtml: "",
      previewSubject: "",
      loading: true,
      error: "",
      forcing: false,
      forceError: "",
    };
  },
  computed: {
    isAdmin() {
      return this.$store?.state?.is_admin === true;
    },
    // Se puede adelantar mientras no haya salido.
    canForceSend() {
      const status = this.campaign?.status;
      return ["draft", "pending", "scheduled", "failed"].includes(status);
    },
    playTitles() {
      const titles = (this.campaign?.plays || []).map((p) => p.title);
      return titles.length ? titles.join(", ").toUpperCase() : "-";
    },
    budgetLabel() {
      const value = this.campaign?.budget_max;
      if (!value) return "NINGUNO";
      return `$ ${Number(value).toLocaleString("es-CL")}`;
    },
    statusClass() {
      const status = this.campaign?.status;
      if (status === "sent") return "camp-status-sent";
      if (status === "failed") return "camp-status-failed";
      if (status === "sending") return "camp-status-sending";
      return "camp-status-pending";
    },
  },
  async created() {
    await this.loadAuth();
    if (!this.isAdmin) {
      this.loading = false;
      return;
    }
    await this.load();
  },
  methods: {
    async forceSend() {
      const ok = window.confirm(
        "¿Enviar la campaña ahora? Sale de inmediato, sin esperar la ventana " +
          "de las 09:00."
      );
      if (!ok) return;
      this.forcing = true;
      this.forceError = "";
      try {
        this.campaign = await forceSendCampaign(this.campaignId);
      } catch (e) {
        const data = e.response?.data;
        this.forceError =
          data?.detail ||
          (Array.isArray(data) ? data[0] : null) ||
          "No se pudo enviar la campaña.";
        console.error(e);
      } finally {
        this.forcing = false;
      }
    },
    async loadAuth() {
      try {
        await this.$store.dispatch("fetchIsAuthenticated");
      } catch (e) {
        console.error(e);
      }
    },
    async load() {
      this.loading = true;
      this.error = "";
      try {
        const [campaign, recipients, preview] = await Promise.all([
          fetchCampaign(this.campaignId),
          fetchCampaignRecipients(this.campaignId),
          previewCampaignById(this.campaignId),
        ]);
        this.campaign = campaign;
        this.recipients = recipients || [];
        this.previewHtml = preview?.html || "";
        this.previewSubject = preview?.subject || "";
      } catch (e) {
        this.error = "No se pudo cargar la campaña.";
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    formatDate(value) {
      if (!value) return "-";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "-";
      return date.toLocaleString("es-CL", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    goBack() {
      window.location.href = "/preventa/";
    },
    goToEdit() {
      window.location.href = `/preventa/${this.campaignId}/editar/`;
    },
  },
};
</script>

<style scoped>
.camp-title {
  max-width: 1080px;
  margin: 0 auto 18px;
}

.camp-title h1 {
  margin: 0;
  font-family: var(--font-family-heading);
  font-size: 26px;
  font-weight: 800;
  letter-spacing: 1.6px;
  color: var(--color-theater-text);
  text-transform: uppercase;
}

.camp-title h2 {
  margin: 2px 0 0;
  font-family: var(--font-family-heading);
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 1.4px;
  color: #e2334c;
}

.camp-panel {
  max-width: 1080px;
  margin: 0 auto;
  background: rgba(8, 5, 5, 0.66);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 26px;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.55);
}

.camp-panel-spaced {
  margin-top: 26px;
}

.camp-panel-header {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 66px;
  padding: 0 32px;
  background: linear-gradient(90deg, #c8102e 0%, #a50d25 100%);
  color: #fff;
  font-family: var(--font-family-heading);
  font-size: 15px;
  font-weight: 800;
  letter-spacing: 1.4px;
}

.camp-panel-body {
  padding: 28px 34px 30px;
}

.camp-status {
  margin-left: auto;
  display: inline-block;
  padding: 5px 14px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.8px;
  background: rgba(0, 0, 0, 0.28);
}

.camp-status-sent {
  color: #b9ffd0;
}

.camp-status-pending {
  color: rgba(255, 255, 255, 0.85);
}

.camp-status-sending {
  color: #ffe4a8;
}

.camp-status-failed {
  color: #ffd0d7;
}

.camp-metrics {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
}

.camp-metric {
  flex: 1 1 180px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 20px 22px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
}

.camp-metric-value {
  font-family: var(--font-family-heading);
  font-size: 30px;
  font-weight: 800;
  letter-spacing: 0.5px;
  color: var(--color-theater-text);
}

.camp-metric-label {
  font-family: var(--font-family-heading);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1px;
  color: rgba(255, 255, 255, 0.6);
}

.camp-warning {
  margin-top: 18px;
  padding: 12px 16px;
  border-radius: 10px;
  background: rgba(200, 16, 46, 0.16);
  color: #ffb3bf;
  font-family: var(--font-family-heading);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.camp-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 20px 26px;
  margin: 28px 0 0;
}

.camp-summary dt {
  font-family: var(--font-family-heading);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 1px;
  color: rgba(255, 255, 255, 0.55);
}

.camp-summary dd {
  margin: 5px 0 0;
  font-family: var(--font-family-heading);
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.6px;
  color: var(--color-theater-text);
  text-transform: uppercase;
}

.camp-table-scroll {
  overflow-x: auto;
}

.camp-table {
  width: 100%;
  border-collapse: collapse;
  color: var(--color-theater-text);
  font-family: var(--font-family-heading);
}

.camp-table th {
  text-align: left;
  padding: 8px 14px 18px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  color: rgba(255, 255, 255, 0.55);
  white-space: nowrap;
}

.camp-table td {
  padding: 14px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.4px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  white-space: nowrap;
}

.camp-table tbody tr:first-child td {
  border-top: none;
}

.camp-cell-name {
  text-transform: uppercase;
}

.camp-empty {
  text-align: center;
  padding: 30px 16px;
  color: rgba(255, 255, 255, 0.6);
  font-weight: 500;
}

.camp-preview {
  display: flex;
  justify-content: center;
}

.camp-preview-subject {
  max-width: 680px;
  margin: 0 auto 12px;
  font-family: var(--font-family-heading);
  font-size: 13px;
  color: #ffffff;
}

.camp-preview-subject span {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.9px;
  color: rgba(255, 255, 255, 0.6);
  margin-right: 6px;
}

.camp-preview-frame {
  width: 100%;
  max-width: 680px;
  height: 720px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 16px;
  background: #ffffff;
}

.camp-actions {
  max-width: 1080px;
  margin: 26px auto 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 20px;
}

.camp-ghost-btn {
  margin-right: auto;
  background: transparent;
  border: none;
  color: var(--color-theater-text);
  cursor: pointer;
  font-family: var(--font-family-heading);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1px;
  transition: opacity 0.2s ease;
}

.camp-ghost-btn:hover {
  opacity: 0.72;
}

/* Adelantar el envío: mismo formato que el primario, en oscuro para que no
   compita con "revisar campaña". */
.camp-force-btn {
  background: #2e2e2e;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 13px 28px;
  font-family: var(--font-family-heading);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 1px;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.camp-force-btn:hover:not(:disabled) {
  transform: translateY(-1px);
}

.camp-force-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

.camp-primary-btn {
  background: linear-gradient(90deg, #c8102e 0%, #a50d25 100%);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 13px 34px;
  font-family: var(--font-family-heading);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 1px;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(200, 16, 46, 0.35);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.camp-primary-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 22px rgba(200, 16, 46, 0.45);
}

.camp-denied {
  max-width: 1080px;
  margin: 0 auto;
  padding: 40px;
  text-align: center;
  background: rgba(8, 5, 5, 0.66);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 26px;
  color: var(--color-theater-text);
  font-family: var(--font-family-heading);
  font-weight: 600;
  letter-spacing: 0.6px;
}

@media (max-width: 700px) {
  .camp-panel-body {
    padding: 22px 16px 24px;
  }
}
</style>
